import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { cdrErr } from "./cdrMeta";

const EMPTY = { kind: "territory", name: "", emails: "", note: "", enabled: true };

/** Add or edit one recipient. `rec` = null (closed), {} (new) or the recipient. */
export function CdrRecipientDialog({ rec, onClose, onSaved }) {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (rec) setF(rec.id ? { ...rec, emails: rec.emails.join(", "), note: rec.note || "" } : EMPTY); }, [rec]);
  const save = async () => {
    setBusy(true);
    const body = { kind: f.kind, name: f.name.trim(), emails: f.emails.split(/[,;\s]+/).filter(Boolean), note: f.note, enabled: f.enabled };
    try {
      if (rec.id) await api.put(`/cdr-dispatch/recipients/${rec.id}`, body);
      else await api.post("/cdr-dispatch/recipients", body);
      toast.success(`${body.name} saved`); onSaved(); onClose();
    } catch (e) { toast.error(cdrErr(e, "Could not save")); } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!rec} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="cdr-rec-dialog">
        <DialogHeader><DialogTitle>{rec?.id ? `Edit ${rec.name}` : "Add a CDR file recipient"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="flex gap-2">
            {["territory", "domain"].map((k) => (
              <button key={k} type="button" onClick={() => setF({ ...f, kind: k })} data-testid={`cdr-rec-kind-${k}`}
                      className={`rounded-full border px-3 py-1 text-xs font-medium capitalize transition-colors ${f.kind === k ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 text-slate-600 hover:border-slate-400"}`}>{k}</button>))}
          </div>
          <label className="block space-y-1"><span className="text-xs font-medium text-slate-600">{f.kind === "territory" ? "Territory (Originating TG)" : "Domain (Originating Span)"}</span>
            <Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder={f.kind === "territory" ? "e.g. solutionsit" : "e.g. example.ca"} className="font-mono text-sm" data-testid="cdr-rec-name" /></label>
          <label className="block space-y-1"><span className="text-xs font-medium text-slate-600">Send to</span>
            <Textarea value={f.emails} onChange={(e) => setF({ ...f, emails: e.target.value })} rows={2} placeholder="billing@partner.ca, ops@partner.ca" className="text-sm" data-testid="cdr-rec-emails" /></label>
          <label className="block space-y-1"><span className="text-xs font-medium text-slate-600">Note (optional)</span>
            <Input value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} className="text-sm" data-testid="cdr-rec-note" /></label>
          <label className="flex items-center justify-between rounded-md border border-slate-200 px-3 py-2 text-xs text-slate-700">Included in the monthly send
            <Switch checked={f.enabled} onCheckedChange={(v) => setF({ ...f, enabled: v })} data-testid="cdr-rec-enabled" /></label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="cdr-rec-cancel">Cancel</Button>
          <Button onClick={save} disabled={busy || !f.name.trim() || !f.emails.trim()} data-testid="cdr-rec-save">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
